import _ from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { useCallbackOne } from 'use-memo-one';

function calcUpdatedOpenValue(filter: IFilter, clickedColumn: string): boolean {
  return filter.column === clickedColumn ? !filter.isOpen : false;
}

function filtersAsObject(filters: IFilter[]): IFilters {
  return filters.reduce((filtersObject, filter) => ({ ...filtersObject, [filter.column]: filter }), {});
}

function addItemsToTableFilters(
  tableFilters: IFilter[],
  selectedFilter: IFilter,
  column: string,
  items: string[],
): IFilter[] {
  const filtersToUpdate = tableFilters.filter(
    (filter) => filter.usingOrder > selectedFilter.usingOrder || filter.usingOrder === 0,
  );

  for (const filter of filtersToUpdate) filter.selectedFilters = [...addSelectedItems(filter, column, items)];

  return [...tableFilters];
}

function calcUsingOrder(filters: IFilter[], selectedFilter: IFilter): number {
  const maxUsingOrder: number = _.maxBy(filters, (filter) => filter.usingOrder)?.usingOrder || 0;

  const currentUsingOrder = selectedFilter.usingOrder;

  return currentUsingOrder === 0 ? maxUsingOrder + 1 : currentUsingOrder;
}

function getCurrentFilter(filters: IFilter[], column: string): IFilter {
  return filters.find((filter: IFilter) => filter.column === column) || filters[0];
}

function getFiltersAsArray(objectLikeFilters: IFilters): IFilter[] {
  return Object.keys(objectLikeFilters).map((filterName: string) => objectLikeFilters[filterName]);
}

function addSelectedItems(filter: IFilter, column: string, items: string[]): ISelectedFilter[] {
  const selectedFilter = filter.selectedFilters.find((filter) => filter.column === column);

  if (selectedFilter) selectedFilter.items = items;
  else filter.selectedFilters = [...filter.selectedFilters, { column, items }];

  return [...filter.selectedFilters];
}

function orderByChecked(items: IListItem[]): IListItem[] {
  return _.orderBy(items, 'checked', 'desc');
}

function setCheckedStateFromPrevItems(alreadyExistingItems: IListItem[], currentItems: IListItem[]): IListItem[] {
  for (const item of alreadyExistingItems)
    item.checked = currentItems.find((existingItem) => existingItem.label === item.label)?.checked || false;

  return alreadyExistingItems;
}

function checkIfFilterExist(existingFilters: ITableFilters, tableName: string, column: string): boolean {
  try {
    const existingFilter = existingFilters[tableName][column];

    if (!!!existingFilter) throw new Error(`filter doesn't exist`);

    return true;
  } catch (error) {
    return false;
  }
}

interface IContext {
  filters: ITableFilters;
  initFilterState: (tableColumn: string, column: string) => void;
  setItems: (tableName: string, column: string, isNextPageFetched: boolean, upcomingItems: IListItem[]) => void;
  setSearch: (tableName: string, column: string, search: string) => void;
  setInputDate: (tableName: string, column: string, date: Partial<IDateInput>) => void;
  setSelectedItems: (tableName: string, column: string, items: string[]) => void;
  setIsUsing: (tableName: string, column: string, isUsing: boolean) => void;
  removeSelectedFilter: (tableName: string, column: string) => void;
  resetPage: (tableName: string, column: string) => void;
  setHasMoreItems: (tableName: string, column: string, hasMoreItems: boolean) => void;
  toggleAllItemsChecking: (tableName: string, column: string) => void;
  toggleIsOpen: (tableName: string, clickedColumn: string) => void;
  toggleSorting: (tableName: string, column: string) => void;
  increasePage: (tableName: string, column: string) => void;
}

const initialState: ITableFilters = {};

const initialContextValues: IContext = {
  filters: initialState,
  initFilterState: (tableName: string, column: string) => null,
  setItems: (tableName: string, column: string, isNextPageFetched: boolean, upcomingItems: IListItem[]) => null,
  setSearch: (tableName: string, column: string, search: string) => null,
  setInputDate: (tableName: string, column: string, date: Partial<IDateInput>) => null,
  setSelectedItems: (tableName: string, column: string, items: string[]) => null,
  setIsUsing: (tableName: string, column: string, isUsing: boolean) => null,
  removeSelectedFilter: (tableName: string, column: string) => null,
  resetPage: (tableName: string, column: string) => null,
  setHasMoreItems: (tableName: string, column: string, hasMoreItems: boolean) => null,
  toggleSorting: (tableName: string, column: string) => null,
  toggleAllItemsChecking: (tableName: string, column: string) => null,
  toggleIsOpen: (tableColumn: string, clickedColumn: string) => null,
  increasePage: (tableName: string, column: string) => null,
};

const Context: React.Context<IContext> = React.createContext<IContext>(initialContextValues);

export const useFiltersContext = (): IContext => React.useContext(Context);

interface ISorting {
  accessor: string;
  asc: boolean;
}

export interface IListItem {
  label: string;
  checked: boolean;
}

export interface IFiltering {
  key: string;
  value: string;
}

export interface ISelectedFilter {
  column: string;
  items: string[];
}
export interface IDateInput {
  from: string;
  to: string;
}

export interface IFilter {
  isOpen: boolean;
  isUsing: boolean;
  column: string;
  items: IListItem[];
  search: string;
  page: number;
  allItemsIsChecked: boolean;
  hasMoreItems: boolean;
  sorting: ISorting;
  selectedFilters: ISelectedFilter[];
  usingOrder: number;
  dateInput: IDateInput;
}

export const createInitFilterState = (column: string): IFilter => ({
  column,
  isOpen: false,
  isUsing: false,
  items: [],
  search: '',
  page: 1,
  allItemsIsChecked: false,
  hasMoreItems: true,
  sorting: {
    accessor: column,
    asc: true,
  },
  selectedFilters: [],
  usingOrder: 0,
  dateInput: {
    from: moment('2020-01-01').format(),
    to: moment().format(),
  },
});

export interface IFilters {
  [key: string]: IFilter;
}

export interface ITableFilters {
  [key: string]: IFilters;
}

const FiltersProvider = ({ children }: { children: React.ReactNode }) => {
  const [filters, setFilters] = useState<ITableFilters>(initialState);

  const updateFilter = useCallbackOne(
    (
      filterTables: ITableFilters,
      tableName: string,
      column: string,
      fieldAsObject: Partial<IFilter>,
    ): ITableFilters => {
      return {
        ...filterTables,
        [tableName]: {
          ...filterTables[tableName],
          [column]: {
            ...filterTables[tableName][column],
            ...fieldAsObject,
          },
        },
      };
    },
    [],
  );

  const updateTable = useCallbackOne(
    (filters: ITableFilters, tableName: string, formatter: (filter: IFilter) => IFilter) => {
      const tableFilters: ITableFilters = {
        [tableName]: {},
      };

      for (const filterName in filters[tableName]) {
        const filter = filters[tableName][filterName];

        tableFilters[tableName][filterName] = formatter(filter);
      }

      return { ...filters, ...tableFilters };
    },
    [],
  );

  const setIsUsing = useCallbackOne((tableName: string, column: string, isUsing: boolean) => {
    setFilters((tableFilters) => updateFilter(tableFilters, tableName, column, { isUsing }));
  }, []);

  const setItems = useCallbackOne(
    (tableName: string, column: string, isNextPageFetched: boolean, upcomingItems: IListItem[]) => {
      setFilters((tableFilters) => {
        const currentItems = tableFilters[tableName][column].items;

        const allItems = isNextPageFetched ? [...currentItems, ...upcomingItems] : upcomingItems;

        const alreadyExistedItems = _.intersectionBy(upcomingItems, allItems, 'label');

        const itemsWithSavedCheckedState = setCheckedStateFromPrevItems(alreadyExistedItems, currentItems);

        const newItems = _.differenceBy(allItems, itemsWithSavedCheckedState);

        const listItems = [...itemsWithSavedCheckedState, ...newItems];

        return updateFilter(tableFilters, tableName, column, {
          items: isNextPageFetched ? orderByChecked(listItems) : listItems,
        });
      });
    },
    [],
  );

  const setSelectedItems = useCallbackOne((tableName: string, column: string, items: string[]) => {
    setFilters((filters) => {
      const tableFilters: IFilter[] = getFiltersAsArray(filters[tableName]);

      const selectedFilter = getCurrentFilter(tableFilters, column);

      selectedFilter.usingOrder = calcUsingOrder(tableFilters, selectedFilter);

      const updatedFilters = addItemsToTableFilters(tableFilters, selectedFilter, column, items);

      const filtersAsTableObject: IFilters = filtersAsObject(updatedFilters);

      return {
        ...filters,
        [tableName]: {
          ...filtersAsTableObject,
        },
      };
    });
  }, []);

  const setSearch = useCallbackOne((tableName: string, column: string, search: string) => {
    setFilters((tableFilters) => updateFilter(tableFilters, tableName, column, { search }));
  }, []);

  const setInputDate = useCallbackOne((tableName: string, column: string, date: Partial<IDateInput>) => {
    setFilters((tableFilters) =>
      updateFilter(tableFilters, tableName, column, {
        dateInput: { ...tableFilters[tableName][column].dateInput, ...date },
      }),
    );
  }, []);

  const setHasMoreItems = useCallbackOne((tableName: string, column: string, hasMoreItems: boolean) => {
    setFilters((tableFilters) => updateFilter(tableFilters, tableName, column, { hasMoreItems }));
  }, []);

  const setAllItemsIsChecked = useCallbackOne((tableName: string, column: string, allItemsIsChecked: boolean) => {
    setFilters((tableFilters) => updateFilter(tableFilters, tableName, column, { allItemsIsChecked }));
  }, []);

  const setSorting = useCallbackOne((tableName: string, column: string, asc: boolean) => {
    setFilters((tableFilters) => updateFilter(tableFilters, tableName, column, { sorting: { asc, accessor: column } }));
  }, []);

  const removeSelectedFilter = useCallbackOne((tableName: string, column: string) => {
    setFilters((filters) =>
      updateTable(filters, tableName, (filter) => ({
        ...filter,
        selectedFilters: filter.selectedFilters.filter((filter) => filter.column !== column),
        isUsing: column === filter.column ? false : filter.isUsing,
        items: filter.items.map((item) => ({ ...item, checked: false })),
        allItemsIsChecked: false,
      })),
    );
  }, []);

  const resetPage = useCallbackOne((tableName: string, column: string) => {
    setFilters((tableFilters) => updateFilter(tableFilters, tableName, column, { page: 1 }));
  }, []);

  const increasePage = useCallbackOne((tableName: string, column: string) => {
    setFilters((tableFilters) =>
      updateFilter(tableFilters, tableName, column, { page: tableFilters[tableName][column].page + 1 }),
    );
  }, []);

  const toggleSorting = useCallbackOne(
    (tableName: string, column: string) => {
      const filter = filters[tableName][column];

      setSorting(tableName, column, !filter.sorting.asc);
    },
    [filters],
  );

  const toggleAllItemsChecking = useCallbackOne(
    (tableName: string, column: string) => {
      const filter = filters[tableName][column];

      const updatedCheckedState = !filter.allItemsIsChecked;

      filter.allItemsIsChecked = updatedCheckedState;

      for (const item of filter.items) item.checked = updatedCheckedState;

      setAllItemsIsChecked(tableName, column, updatedCheckedState);

      setItems(tableName, column, false, [...filter.items]);
    },
    [filters],
  );

  const initFilterState = useCallbackOne((tableName: string, column: string) => {
    setFilters((existingFilters: ITableFilters) => ({
      ...existingFilters,
      [tableName]: {
        ...existingFilters[tableName],
        [column]: checkIfFilterExist(existingFilters, tableName, column)
          ? existingFilters[tableName][column]
          : createInitFilterState(column),
      },
    }));
  }, []);

  const toggleIsOpen = useCallbackOne((tableName: string, clickedColumn: string) => {
    setFilters((filters) =>
      updateTable(filters, tableName, (filter) => ({
        ...filter,
        isOpen: calcUpdatedOpenValue(filter, clickedColumn),
      })),
    );
  }, []);

  return (
    <Context.Provider
      value={{
        filters,
        toggleIsOpen,
        setItems,
        setSelectedItems,
        setSearch,
        setInputDate,
        setIsUsing,
        removeSelectedFilter,
        resetPage,
        setHasMoreItems,
        toggleSorting,
        toggleAllItemsChecking,
        initFilterState,
        increasePage,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default FiltersProvider;
